import CircularProgress from '@material-ui/core/CircularProgress';
import React, { useEffect, useState } from 'react';
import axios from 'src/utils/axios';
import { useTranslation } from 'react-i18next';
import {
  Grid, Card, CardContent, Container, Backdrop, Typography, Divider, Paper,
  OutlinedInput, InputAdornment, Button, TableContainer, Table, TableHead,
  TableRow, TableCell, TableBody, TablePagination, IconButton, Box
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import AddBoxIcon from '@material-ui/icons/AddBox';
import { makeStyles } from '@material-ui/styles';
import Page from 'src/components/Page';

const useStyles = makeStyles((theme) => ({
  page: { paddingTop: theme.spacing(2), paddingBottom: theme.spacing(2) },
  backdrop: { zIndex: 1500, color: '#fff' },

  // CARD
  card: { borderRadius: 16, boxShadow: '0 8px 24px rgba(16,24,40,0.06)' },

  // HEADER
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  title: { fontSize: 18, fontWeight: 700, color: '#1f2937' },

  // SEARCH BAR
  input: {
    height: 44,
    borderRadius: 12,              
    background: '#fff'
  },
  searchBtn: {
    height: 44, borderRadius: 12, padding: '0 18px',
    background: 'linear-gradient(90deg,#00B2FF,#0079FF)',
    color: '#fff', fontWeight: 700, letterSpacing: .2,
    textTransform: 'none', boxShadow: '0 6px 18px rgba(0,121,255,.25)',
    '&:hover': { background: 'linear-gradient(90deg,#00A5F0,#006EF0)' }
  },

  // TABLE
  tableWrap: { borderRadius: 14, overflow: 'hidden', boxShadow: '0 6px 18px rgba(16,24,40,.06)' },
  thead: {
    background: '#F5F7FB',
    '& th': { fontWeight: 700, color: '#334155', fontSize: 13, borderBottom: '1px solid #E5E7EB' }
  },
  row: {
    '&:nth-of-type(odd)': { backgroundColor: '#FCFCFD' },
    '&:hover': { backgroundColor: '#F2F7FF' }
  },
  addIconBtn: {
    background: '#E8F2FF', color: '#0B74FF', borderRadius: 10,
    '&:hover': { background: '#D9EAFF' }
  },

  // EMPTY STATE
  empty: {
    padding: theme.spacing(6, 2), textAlign: 'center', color: '#6b7280',
    fontSize: 14
  }
}));

function ConsumerSearch({ handleClick, ConsumerPhone }) {
  const classes = useStyles();
  const { t } = useTranslation('overview');

  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consumerId, setConsumerId] = useState('');
  const [filteredConsumers, setFilteredConsumers] = useState([]);
  const [consumerAddresses, setConsumerAddresses] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);

  const consumerData = {
    consumerId: filteredConsumers.Id,
    consumerFullName: filteredConsumers.Name,
    consumerPhoneNumber: filteredConsumers.Phone,
    consumerEmail: filteredConsumers.Email
  };

  const GetConsumerForMaterialOrder = async () => {
    setLoading(true);
    try {
      // phoneNumber array olabilir
      const phoneValue = Array.isArray(phoneNumber)
        ? phoneNumber[0]?.Phone || ""
        : phoneNumber || "";

      // ✅ Boş arama kontrolü
      if ((!consumerId || consumerId === "0") && (!phoneNumber || phoneNumber.length === 0)) {
        setFilteredConsumers([]);
        setConsumerAddresses([]);
        setLoading(false);
        return;
      }

      // Parametreleri normalize et
      const nameParam = name?.trim() ? name.toLowerCase() : "null";
      const surnameParam = surname?.trim() ? surname.toLowerCase() : "null";
      const consumerIdParam = consumerId?.toString().trim() ? consumerId : "null";
      const phoneParam = phoneValue ? phoneValue : "null";

      const url = `/GetConsumerForMaterialOrder/${nameParam}/${surnameParam}/${consumerIdParam}/${phoneParam}`;

      const { data } = await axios.get(url);

      let parsedData = [];
      try {
        parsedData = typeof data === "string" ? JSON.parse(data) : data;
      } catch {
        parsedData = [];
      }

      if (Array.isArray(parsedData) && parsedData.length > 0) {
        setFilteredConsumers(parsedData[0]);
        setConsumerAddresses(parsedData);
      } else {
        setFilteredConsumers([]);
        setConsumerAddresses([]);
      }
    } catch (e) {
      console.error("Consumer search error:", e);
      setFilteredConsumers([]);
      setConsumerAddresses([]);
    } finally {
      setLoading(false);
    }
  };

   // Mask helper
  const mask = (v) => {
    if (!v) return "";
    if (v.length <= 3) return v;
    return v.slice(0, 3) + "*".repeat(v.length - 3);
  };

  useEffect(() => { setPhoneNumber(ConsumerPhone || ''); }, [ConsumerPhone]);

  return (
    <Page className={classes.page}>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container maxWidth="xl">
        <Card className={classes.card}>
          <CardContent>
            {/* SEARCH BAR – responsive: xs=12 sm=6 md=3 + buton */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <OutlinedInput
                  className={classes.input}
                  fullWidth
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t("consumerPhone")}
                  startAdornment={<InputAdornment position="start">📞</InputAdornment>}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <OutlinedInput
                  className={classes.input}
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("consumerName")}
                  startAdornment={<InputAdornment position="start">👤</InputAdornment>}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <OutlinedInput
                  className={classes.input}
                  fullWidth
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder={t("consumerSurname")}
                  startAdornment={<InputAdornment position="start">🪪</InputAdornment>}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <OutlinedInput
                  className={classes.input}
                  fullWidth
                  value={consumerId}
                  onChange={(e) => setConsumerId(e.target.value)}
                  placeholder={t("consumerId")}
                  startAdornment={<InputAdornment position="start">#</InputAdornment>}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={1}>
                <Button onClick={GetConsumerForMaterialOrder} fullWidth className={classes.searchBtn}>
                  <SearchIcon style={{ marginRight: 6 }} />
                </Button>
              </Grid>
            </Grid>

            <Divider style={{ margin: '18px 0 12px' }} />

            {/* TABLE / EMPTY */}
            {consumerAddresses.length === 0 ? (
              <Box className={classes.empty}>
                <Typography style={{ fontWeight: 600, marginBottom: 6 }}>
                  {t('lblAreaCodeInfo')}
                </Typography>
                <Typography>
                  {t("searchHint")} <b><SearchIcon style={{ marginRight: 6 }} /></b>
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} className={classes.tableWrap}>
                <Table stickyHeader size="small" aria-label="consumer table">
                  <TableHead className={classes.thead}>
                    <TableRow>
                      <TableCell width={56} />
                      <TableCell>{t('lblConsumerName')}</TableCell>
                      <TableCell>{t('lblConsumerSurname')}</TableCell>
                      <TableCell>{t('lblConsumerPhone')}</TableCell>
                      <TableCell>{t('lblConsumerAddress')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rowsPerPage > 0
                      ? consumerAddresses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : consumerAddresses
                    ).map((row) => (
                      <TableRow key={row.AddressId} className={classes.row} hover>
                        <TableCell align="center">
                          <IconButton
                            className={classes.addIconBtn}
                            size="small"
                            onClick={() => handleClick(
                              {
                                consumerId: filteredConsumers.Id,
                                consumerFullName: filteredConsumers.Name,
                                consumerPhoneNumber: filteredConsumers.Phone,
                                consumerEmail: filteredConsumers.Email
                              },
                              row
                            )}
                          >
                            <AddBoxIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell>{filteredConsumers.FirstName}</TableCell>
                        <TableCell>{filteredConsumers.Surname}</TableCell>
                        <TableCell>{row.Phone}</TableCell>
                        <TableCell>{mask(row.Address)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <TablePagination
                  rowsPerPageOptions={[5, 10]}
                  component="div"
                  count={consumerAddresses.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={(e, newPage) => setPage(newPage)}
                  onChangeRowsPerPage={(e) => {
                    setRowsPerPage(+e.target.value);
                    setPage(0);
                  }}
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
}

export default ConsumerSearch;
